'use client';

import React from 'react';
import { useTranslations } from 'next-intl';
import { motion } from 'framer-motion';
import { Award, Building2, Factory } from 'lucide-react';
import { format } from 'date-fns';

interface CertificationCardProps {
    name: string;
    buildingType: string;
    imageUrl: string | null;
    siteLocation: string;
    offsiteLocation: string;
    id: string;
    status?: string;
    updatedAt?: string;
    creatorName?: string;
    onSelect?: () => void;
}

export const CertificationCard = ({
    name,
    buildingType,
    imageUrl,
    siteLocation,
    offsiteLocation,
    id,
    status,
    updatedAt,
    creatorName,
    onSelect,
}: CertificationCardProps) => {
    const t = useTranslations('landing.certificationCard');
    return (
        <a href={`/certifications/${id}`} className="block h-full select-none group/link">
            <motion.div
                className="bg-white border border-slate-200 h-full overflow-hidden flex flex-col group cursor-pointer relative"
                whileHover={{ y: -4 }}
                transition={{ duration: 0.2 }}
            >
                {onSelect && (
                    <div
                        className="absolute inset-0 z-20 cursor-pointer"
                        onClick={(e) => { e.preventDefault(); onSelect(); }}
                    />
                )}
                <div className="relative aspect-video bg-slate-100 overflow-hidden">
                    {imageUrl ? (
                        // eslint-disable-next-line @next/next/no-img-element
                        <img
                            src={imageUrl}
                            alt={name}
                            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                        />
                    ) : (
                        <div className="w-full h-full bg-gradient-to-br from-slate-100 to-slate-200 flex items-center justify-center">
                            <Award className="w-10 h-10 text-slate-300" />
                        </div>
                    )}
                    {updatedAt && (
                        <div className="absolute top-0 left-0 bg-white text-black p-2 flex flex-col items-center justify-center min-w-[3.5rem] shadow-sm z-10">
                            <span className="text-xs font-bold uppercase tracking-wider">{format(new Date(updatedAt), 'MMM')}</span>
                            <span className="text-sm font-light">{format(new Date(updatedAt), 'yyyy')}</span>
                        </div>
                    )}
                    {creatorName && (
                        <div className="absolute bottom-0 left-0 bg-white/90 backdrop-blur-sm px-2 py-1 text-xs text-slate-700 truncate max-w-[70%] z-10">
                            {creatorName}
                        </div>
                    )}
                    <div className="absolute top-3 right-3 flex flex-col items-end gap-1 z-10">
                        {buildingType && (
                            <span className="bg-white/90 backdrop-blur-sm px-2 py-1 text-xs font-medium uppercase tracking-wider text-slate-700 shadow-sm">
                                {buildingType}
                            </span>
                        )}
                        {status && (
                            <span className={`bg-white/90 backdrop-blur-sm px-2 py-1 text-xs font-medium uppercase tracking-wider shadow-sm ${status === 'certified' ? 'text-emerald-600' : 'text-slate-700'}`}>
                                {status}
                            </span>
                        )}
                    </div>
                </div>

                <div className="p-3 flex flex-col flex-grow">
                    <h3 className="text-base font-medium text-slate-900 mb-3 leading-tight group-hover/link:underline decoration-1 underline-offset-4">
                        {name}
                    </h3>
                    <div className="flex flex-col gap-2 mt-auto text-xs text-slate-500">
                        {siteLocation && (
                            <div className="flex items-center gap-2 min-w-0" title={t('site')}>
                                <Building2 className="w-3 h-3 flex-shrink-0 text-blue-500" />
                                <span className="truncate">{siteLocation}</span>
                            </div>
                        )}
                        {offsiteLocation && (
                            <div className="flex items-center gap-2 min-w-0" title={t('offsite')}>
                                <Factory className="w-3 h-3 flex-shrink-0 text-amber-500" />
                                <span className="truncate">{offsiteLocation}</span>
                            </div>
                        )}
                    </div>
                </div>
            </motion.div>
        </a>
    );
};
